import React, { useState, useEffect, useRef } from 'react'
import {
    Box, Button, Card, CardHeader, CardContent, Typography,
    Grid, FormGroup, FormLabel, OutlinedInput, FormControl, Dialog, DialogContent, Accordion, AccordionSummary, AccordionDetails
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { FileCopy as CopyIcon, Email as EmailIcon, CodeOutlined, VerifiedUserOutlined, LockOutlined, ExpandMore as ExpandMoreIcon } from '@material-ui/icons'
import componentStyles from 'assets/theme/views/admin/react-bs-table.js'
import { NotificationManager } from 'react-notifications'
import config from 'config/api/Config'
import ProviderAPI from 'config/api/ProviderAPI'
import GuestAPI from 'config/api/GuestAPI'

const useStyles = makeStyles(componentStyles)

function WebsiteVerificationCard({ companyDetails, updateStage, guest=false }) {
    const classes = useStyles()
    const api = guest ? new GuestAPI() : new ProviderAPI()

    const badgeVerified = companyDetails.badgeVerified || false
    const scriptUrl = `https://${config.s3.BUCKET}.s3.${config.s3.REGION}.amazonaws.com/public/badges/${companyDetails.companyId}.js`
    const embedCode = `<div id="ee-badge"></div>\n<script src="${scriptUrl}"></script>`

    const [showDevDialog, setShowDevDialog] = useState(false)
    const [devEmail, setDevEmail] = useState('')
    const [checkingStatus, setCheckingStatus] = useState(false)
    const [localVerified, setLocalVerified] = useState(badgeVerified)
    const [showSubmitWarning, setShowSubmitWarning] = useState(false)

    const previewRef = useRef(null)
    useEffect(() => {
        if (previewRef.current) {
            previewRef.current.innerHTML = '<div id="ee-badge"></div>'
        }
        const script = document.createElement('script')
        script.src = scriptUrl
        script.async = true
        if (previewRef.current) {
            previewRef.current.appendChild(script)
        }
        return () => {
            if (previewRef.current) {
                previewRef.current.innerHTML = ''
            }
        }
    }, [scriptUrl])

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(embedCode)
            NotificationManager.success('Embed code copied to clipboard')
        } catch {
            NotificationManager.error('Could not copy to clipboard')
        }
    }

    const handleEmailToDeveloper = () => {
        setShowDevDialog(true)
    }

    const handleSendToDeveloper = async () => {
        try {
            const response = await api.sendDeveloperInstructions(companyDetails.url, devEmail)
            console.log(response)
            NotificationManager.success('Instructions sent to ' + devEmail)
            setShowDevDialog(false)
            setDevEmail('')
        } catch (e) {
            NotificationManager.error('Could not check badge status')
        }
    }

    const handleBadgeCheck = async () => {
        setCheckingStatus(true)
        try {
            const response = await api.checkWebsiteBadge(companyDetails.companyId, companyDetails.url)
            console.log(response)
            setLocalVerified(response.verified)
            NotificationManager.success('Status refreshed')
        } catch (e) {
            NotificationManager.error('Could not check badge status')
        }
        setCheckingStatus(false)
    }

    const handleSubmitClick = () => {
        if (!localVerified) {
            setShowSubmitWarning(true)
        } else {
            updateStage()
        }
    }

    const handleContinueSubmit = () => {
        setShowSubmitWarning(false)
        updateStage()
    }

    return (
        <Card>
            <CardHeader
                subheader={
                    <Box display='flex' style={{ justifyContent: 'space-between' }}>
                        <Box>
                            <Box component={Typography} variant='h2' marginBottom='0!important'>
                                <Box component='span'>Website Verification</Box>
                            </Box>
                            <Box
                                component='p'
                                fontSize='.875rem'
                                marginBottom='0'
                                marginTop='0'
                                lineHeight='1.7'
                                fontWeight='300'
                            >
                                Complete the steps below to verify your business and enable your public profile.
                            </Box>
                        </Box>
                    </Box>
                }
                classes={{ root: classes.cardHeaderRoot }}
            />
            <CardContent>
                {/* Approved Installer Badge Preview above accordion */}
                <FormGroup>
                    <FormLabel>Approved Installer Badge Preview</FormLabel>
                    <Box mt={1} mb={2}>
                        <Typography variant="body2" color="#8898aa">
                            The verified installer is a trust badge that you can include on your website. Once this badge is picked up by our platform, customers will be able to enquire with your company directly from your public profile. 
                        </Typography>
                    </Box>
                    <Box
                        ref={previewRef}
                        p={2}
                        mb={2}
                        style={{
                            border: '1px solid #eee',
                            borderRadius: 8,
                            background: '#fafbfc',
                            minHeight: 80,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <div id="ee-badge"></div>
                    </Box>
                </FormGroup>
                {/* Accordion Step 1: Standard border */}
                <Box style={{ border: '1px solid #eee', borderRadius: 8, marginBottom: 12 }}>
                    <Accordion defaultExpanded style={{
                        border: 'none',
                        boxShadow: 'none',
                        borderRadius: 8
                    }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ minHeight: 56 }}>
                            <Box display='flex' alignItems='center' width='100%'>
                                <CodeOutlined style={{ color: '#229626', marginRight: 16, height: 24, width: 24, verticalAlign: 'middle' }} />
                                <Typography variant='h3' style={{ display: 'flex', alignItems: 'center', margin: 0 }}>Step 1: Add the Badge Code to your Website</Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails style={{ display: 'block' }}>
                            <FormGroup>
                                <Box mt={1} mb={2}>
                                    <Typography variant="body2" color="#8898aa">
                                        Put the following HTML on to your website and it will load your verified installer badge. Once you refresh the page, our system will pick it up automatically and your profile will be approved.
                                    </Typography>
                                </Box>
                                <FormControl component={Box} fullWidth>
                                    <OutlinedInput
                                        multiline
                                        rows={4}
                                        value={embedCode}
                                        readOnly
                                        style={{ fontFamily: 'monospace' }}
                                        disabled
                                    />
                                </FormControl>
                            </FormGroup>
                            <Box display='flex' justifyContent='center' mt={2}>
                                <Button
                                    variant='outlined'
                                    color='primary'
                                    onClick={copyToClipboard}
                                    style={{ marginRight: '1rem' }}
                                >
                                    Copy Embed Code to Clipboard
                                </Button>
                                {!guest &&
                                    <Button
                                        variant='outlined'
                                        color='primary'
                                        onClick={handleEmailToDeveloper}
                                    >
                                        Email Instructions to your Web Developer
                                    </Button>
                                }   
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>
                {/* Accordion Step 2: Standard border */}
                <Box style={{ border: '1px solid #eee', borderRadius: 8, marginBottom: 12 }}>
                    <Accordion style={{
                        border: 'none',
                        boxShadow: 'none',
                        borderRadius: 8
                    }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ minHeight: 56 }}>
                            <Box display='flex' alignItems='center' width='100%'>
                                <VerifiedUserOutlined style={{ color: '#229626', marginRight: 16, height: 24, width: 24, verticalAlign: 'middle' }} />
                                <Typography variant='h3' style={{ display: 'flex', alignItems: 'center', margin: 0 }}>Step 2: Check Badge Status</Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails style={{ display: 'block' }}>
                            <FormGroup>
                                <Box mt={1} mb={2}>
                                    <Typography variant="body2" color="#8898aa">
                                        Once the badge code is on your homepage, click the button below to check if it has been detected. If it has, your profile will be approved automatically.
                                    </Typography>
                                </Box>
                            </FormGroup>
                            {!localVerified &&
                                <Box display='flex' alignItems='center' mt={1} justifyContent='center'>
                                    <Button
                                        variant='outlined'
                                        color='primary'
                                        onClick={handleBadgeCheck}
                                        disabled={checkingStatus}
                                    >
                                        {checkingStatus ? 'Checking...' : 'Refresh Badge Status'}
                                    </Button>
                                </Box>
                            }
                            <Box display='flex' alignItems='center' mt={1} justifyContent='center'>
                                <Typography
                                    variant='body2'
                                    style={{
                                        color: localVerified ? '#229626' : '#cc0000',
                                        fontWeight: 500
                                    }}
                                >
                                    {localVerified
                                        ? '✅ Badge is live on your homepage'
                                        : '❌ Badge not detected on your homepage'}
                                </Typography>
                            </Box>
                            {/* Highlighted warning for badge requirement - switched to yellow warning style */}
                            <Box mt={2} mb={2} style={{ border: '1px solid #ffeb3b', borderRadius: 4, backgroundColor: '#fff8e1', padding: '12px' }}>
                                <Typography variant="body2" style={{ color: '#ff8f00', lineHeight: '1.6' }}>
                                    Your profile will remain published on EnergyEfficiency.ie only if the badge remains present on your website. We regularly check for the badge on your homepage, and if it is removed, your profile will be unpublished immediately. To ensure uninterrupted visibility and lead delivery, please keep the badge active on your site at all times.
                                </Typography>
                            </Box>
                            {!guest &&
                                <Box display='flex' justifyContent='flex-end' mt={2}>
                                    <Button
                                        variant='contained'
                                        color='primary'
                                        onClick={handleSubmitClick}
                                        style={{ marginRight: '1rem' }}
                                    >
                                        Submit Profile
                                    </Button>
                                </Box>
                            }   
                        </AccordionDetails>
                    </Accordion>
                </Box>
                {/* Accordion Step 3: Standard border */}
                <Box style={{ border: '1px solid #eee', borderRadius: 8 }}>
                    <Accordion style={{
                        border: 'none',
                        boxShadow: 'none',
                        borderRadius: 8
                    }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ minHeight: 56 }}>
                            <Box display='flex' alignItems='center' width='100%'>
                                <LockOutlined style={{ color: '#229626', marginRight: 16, height: 24, width: 24, verticalAlign: 'middle' }} />
                                <Typography variant='h3' style={{ display: 'flex', alignItems: 'center', margin: 0 }}>Step 3: Why do we need this?</Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails style={{ display: 'block' }}>
                            <Box mt={1} mb={2}>
                                <ul style={{ color: '#8898aa', paddingLeft: 24, margin: 0 }}>
                                    <li style={{ marginBottom: 8 }}>
                                        <Typography variant="body2" component="span">
                                            We display Google reviews directly on your profile. These must be pulled from the Google Business Profile associated with your verified website.
                                        </Typography>
                                    </li>
                                    <li style={{ marginBottom: 8 }}>
                                        <Typography variant="body2" component="span">
                                            We forward leads to the email address linked to your badge, and we need to ensure this matches the verified business domain.
                                        </Typography>
                                    </li>
                                    <li style={{ marginBottom: 8 }}>
                                        <Typography variant="body2" component="span">
                                            This verification ensures we’re recommending real, legitimate providers — no impersonators or lead hijackers.
                                        </Typography>
                                    </li>
                                    <li>
                                        <Typography variant="body2" component="span">
                                            EnergyEfficiency.ie stands behind every badge we display. This snippet allows us to confirm your identity and protect the integrity of the leads we generate for your business.
                                        </Typography>
                                    </li>
                                    <li>
                                        <Typography variant="body2" component="span">
                                            Your profile will remain published on EnergyEfficiency.ie only if the badge remains present on your website. We regularly check for the badge on your homepage, and if it is removed, your profile will be unpublished immediately. To ensure uninterrupted visibility and lead delivery, please keep the badge active on your site at all times.
                                        </Typography>
                                    </li>
                                </ul>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </CardContent>
            <Dialog open={showDevDialog} onClose={() => setShowDevDialog(false)} fullWidth maxWidth='sm'>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} style={{ marginBottom: '1rem' }}>
                            <Box component={Typography} variant='h3'>
                                <Box component='span'>Send Instructions to Developer</Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <FormGroup>
                                <FormLabel>Developer Email Address</FormLabel>
                                <OutlinedInput
                                    value={devEmail}
                                    onChange={(e) => setDevEmail(e.target.value)}
                                    fullWidth
                                />
                            </FormGroup>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2">
                                We will send an email to the developer with the embed code and instructions to add your badge to the website.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Box display='flex' justifyContent='flex-end' p={2}>
                                <Button color='primary' variant='outlined' onClick={() => setShowDevDialog(false)} style={{ marginRight: '1rem' }}>Cancel</Button>
                                <Button color='primary' variant='contained' onClick={handleSendToDeveloper} disabled={!devEmail}>Send Instructions</Button>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
            <Dialog open={showSubmitWarning} onClose={() => setShowSubmitWarning(false)} fullWidth maxWidth='sm'>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} style={{ marginBottom: '1rem' }}>
                            <Box component={Typography} variant='h3'>
                                <Box component='span'>Badge Not Verified</Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} style={{ marginBottom: '1rem' }}>
                            <Typography variant="body2">
                                Your badge has not been detected on your homepage. You can submit your profile without this, but your profile page will not go live on Energy Efficiency until your badge has been added to your website homepage.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Box display='flex' justifyContent='space-between' p={2}>
                                <Button color='primary' variant='outlined' onClick={() => setShowSubmitWarning(false)} style={{ marginRight: '1rem' }}>Cancel</Button>
                                <Button color='primary' variant='contained' onClick={handleContinueSubmit}>Submit Without Verification</Button>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
        </Card>
    )
}

export default WebsiteVerificationCard